import React from "react";
import { motion } from "framer-motion";
import { ArrowForward, AccessTime, Place } from "@mui/icons-material";

const formatEventDate = (dateStr) => {
  if (!dateStr) return "Date TBA";
  const datePart = dateStr.split(/\u2014|\u00e2\u20ac\u201d/)[0].trim();
  const date = new Date(datePart);

  if (Number.isNaN(date.getTime())) return datePart;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const getEventTime = (dateStr = "") =>
  dateStr.split(/\u2014|\u00e2\u20ac\u201d/)[1]?.trim() || "";

// The original events feed predates the rich event editor. Some legacy rows
// therefore contain the full announcement (including HTML/Markdown) where a
// compact card title is expected. Keep the stored content intact, but make the
// homepage summary readable and bounded.
const toEventSummaryText = (value = "") =>
  String(value)
    .replace(/<\s*br\s*\/?\s*>/gi, " ")
    .replace(/<\/(?:p|div|li|h[1-6])\s*>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\*{1,3}/g, "")
    .replace(/\s+/g, " ")
    .trim();

const truncateEventText = (value, limit) =>
  value.length > limit ? `${value.slice(0, limit - 1).trim()}…` : value;

const getEventTitle = (event = {}) => {
  const text = toEventSummaryText(event.title || event.eventTitle || "");
  if (!text) return "Udyog Connect Event";

  const firstSentence = text.split(/(?<=[.!?])\s+/)[0] || text;
  return truncateEventText(firstSentence, 118);
};

const EventSection = ({ events, loading, onView }) => {
  return (
    <motion.article
      className="event-card ne-card"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      viewport={{ once: true }}
    >
      <div className="event-header card-header">
        <span className="section-kicker">Calendar</span>
        <h3>Upcoming Events</h3>
      </div>

      <div className="event-list">
        {loading &&
          Array.from({ length: 3 }).map((_, i) => (
            <div className="event-item ne-skeleton" key={i} />
          ))}

        {!loading && events.length === 0 && (
          <div className="home-empty-state">No upcoming events yet.</div>
        )}

        {!loading &&
          events.map((e, i) => {
            const title = getEventTitle(e);
            const venue = toEventSummaryText(e.description);

            return (
              <motion.div
                key={e.id || i}
                className="event-item"
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04 }}
                viewport={{ once: true }}
              >
                <div className="event-date">{formatEventDate(e.date)}</div>

                <div className="event-body">
                  <h4 title={toEventSummaryText(e.title)}>{title}</h4>

                  {venue && (
                    <p className="event-location" title={venue}>
                      <Place fontSize="inherit" /> {venue}
                    </p>
                  )}

                  {e.date && getEventTime(e.date) && (
                    <p className="event-time">
                      <AccessTime fontSize="inherit" /> {getEventTime(e.date)}
                    </p>
                  )}
                </div>

                {e.status && (
                  <button
                    className={`event-status event-status-button ${e.status}`}
                    type="button"
                    onClick={() => onView(e.id)}
                    aria-label={`Open ${title}`}
                  >
                    {e.status}
                  </button>
                )}

                <button
                  className="event-btn"
                  type="button"
                  onClick={() => onView(e.id)}
                >
                  View <ArrowForward fontSize="small" />
                </button>
              </motion.div>
            );
          })}
      </div>
    </motion.article>
  );
};

export default EventSection;
